// src/com/address_info/AddressInfoListForSms.js
import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Box } from '@mui/system';
import { Button } from '@mui/material';
import PostAddIcon from '@mui/icons-material/PostAdd';
import MobileFriendlyIcon from '@mui/icons-material/MobileFriendly';
import FlawedList from '../message/FlawedList';
import FlexyTable from '../common/flexytable/FlexyTable';
import ValueUtil from '../../model/ValueUtil';
import { AppObject, AppPalette, AppWord, TableColumnType } from '../../model/AppConst';
import { AddressInfoDictionary, LIST_ADDRESS_INFO } from './AddressInfoGql';
import { useLazyQuery, useReactiveVar } from '@apollo/client';
import { addressInfoListRepo } from '../../model/CvoModel';
import ResponseAlert from '../message/ResponseAlert';
import SearchBox from '../common/SearchBox';
import { ContentHeader, HeaderLeft, HeaderRight } from '../common/CvoStyled';
import { useSnackbar } from 'notistack';

const LocalTitle = '주소목록'; // ###################################

const AddressInfoListContainer = styled(Box)({
	flexGrow:1, display:'flex', flexDirection:'column'
});


const Dict = AddressInfoDictionary.inputMap;
const TableColumns = [
    { id: 'fullname', label: Dict.fullname.label, width: 120, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
    { id: 'addrGroupName', label: Dict.addrGroupId.label, width: 100, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
    { id: 'company', label: Dict.company.label, width: 120, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
    { id: 'department', label: Dict.department.label, width: 120, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
    { id: 'workRank', label: Dict.workRank.label, width: 80, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
    { id: 'job', label: Dict.job.label, width: 80, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
    { id: 'hpNo', label: Dict.hpNo.label, width: 120, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
    { id: 'telNo', label: Dict.telNo.label, width: 120, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
    { id: 'zipCode', label: Dict.zipCode.label, width: 80, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
    { id: 'address1', label: Dict.address1.label, width: 150, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
    // { id: 'address2', label: Dict.address2.label, width: 120, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
    //{ id: 'email', label: Dict.email.label, width: 120, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
    { id: 'etc', label: Dict.etc.label, width: 120, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
    // { id: 'senderYn', label: Dict.senderYn.label, width: 120, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
    { id: 'regDate', label: '등록일', width: 120, show: true, option:{align: 'inherit', type: TableColumnType.DATE} }
];

const MinSecReloadInterval = 5000;

export default function AddressInfoListForSms({
    onRequestAddToReceiver,
    onRequestAddAllToReceiver
}) {
    const addressInfoList = useReactiveVar(addressInfoListRepo);
    const [search, setSearch] = useState('');
    const [responseAlert, setResponseAlert] = useState(null);
    const { enqueueSnackbar } = useSnackbar();
    const loadedRef = useRef(0);

    // ##### Call GraphQL to get List #####
    const [getAddressInfoList, responseList] = useLazyQuery(LIST_ADDRESS_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
		onError: (error) => {setResponseAlert({open:true, error: error, title: "주소록 조회 오류"})}
    });

    useEffect(()=>{
        getAddressInfoList();
    }, []);

    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetList = (data, clientOption) => {
        if(data.addressInfoList) {
            loadedRef.current = (new Date()).getTime();
            addressInfoListRepo(data.addressInfoList);
        }
    };

    const onChangeSearch = (text) => {
        setSearch(text);
    };

    const onRequestReload = () => {
        const diff = (new Date()).getTime() - loadedRef.current;
        if(diff > MinSecReloadInterval) {
            getAddressInfoList();
        }
        else {
            enqueueSnackbar(AppWord.NO_RELOAD_IN_5_SEC,
                {variant:AppPalette.WarnColor, autoHideDuration:1500});
        }
    };
    
    const onClickAddAll = () => {
        const all = addressInfoList.filter(v => v.hpNo && v.hpNo.length >= 10).map(v => ({tel: v.hpNo, name: v.fullname}));
        if(all.length > 0) onRequestAddAllToReceiver(all);
    };

    const onClickAdd = (row) => {
        if(row.hpNo && row.hpNo.length >= 10) {
            onRequestAddToReceiver({tel: row.hpNo, name: row.fullname});
        }
        else {
            enqueueSnackbar(row.fullname + '의 휴대전화번호가 올바르지 않습니다.',
                {variant:AppPalette.WarnColor, autoHideDuration:1500});
        }
    };

    const onClickToolOnRow = (toolId, row) => {
        switch(toolId) {
            case AppWord.CMD_ADD: onClickAdd(row); break;
            default: return;
        }
    };

    // Handling list event
    if(ValueUtil.flawedListResponse(responseList))
        return <FlawedList title={LocalTitle} gqlResponse={responseList} />;

    return(
        <AddressInfoListContainer>
            <ContentHeader>
                <HeaderLeft>
                    <SearchBox onChange={onChangeSearch} value={search}/>
                </HeaderLeft>
                <HeaderRight>
                    <Button
                        startIcon={<PostAddIcon />}
                        variant='contained'
                        onClick={onClickAddAll}
                        color={AppPalette.InheritColor}
                    >
                        모두 추가
                    </Button>
                </HeaderRight>
            </ContentHeader>
            <FlexyTable
                name="address_info_list_for_sms"
                uniqueKey="addressId"
                columns={TableColumns}
                records={addressInfoList}
                search={search}
                tools={[
                    {id:AppWord.CMD_ADD, icon:<MobileFriendlyIcon fontSize='small' />},
                ]}
                onClickTool={onClickToolOnRow}
                onRequestReload={onRequestReload}
                onClickOneRow={onClickAdd}
                defaultSorter={{id:'fullname', type: TableColumnType.TEXT}}
            />
            <ResponseAlert open={responseAlert ? responseAlert.open : false}
                alertData={responseAlert}
                onClose={() => {setResponseAlert(null)}}/>
        </AddressInfoListContainer>
    );

}
